// Canonical report-package metadata — single source of truth for every
// surface in the FE that lists Marcus-pushed report editions.
//
// Why this file exists
// --------------------
// Two surfaces probe `farms/{id}/reports/{packageId}/years/...` for
// edition data:
//
//   1. `frontend/src/pages/FarmReportsPage.js`         — customer-facing.
//   2. `frontend/src/pages/admin/ReportsAdmin.js`      — admin-facing
//                                                        Edition Lock Manager.
//
// Pre-2026-05-19 each surface kept its own hardcoded list of package ids,
// which silently drifted out of sync the moment Marcus rolled out the
// 7-code suite (`lhr`, `lhr+`, `lhb`, `sab`, `fop`, `vpa`, `esr` —
// REPORT_SUITE_HANDOFF_to_richard.md §2, 2026-05-19). Customers saw the
// new editions; admins didn't. This module ensures both surfaces probe
// the exact same code list, so future package additions only require
// one edit.
//
// 2026-10-15: the customer Reports page retires `sab` and `fop` (see
// `CUSTOMER_RETIRED_PACKAGE_IDS`). They stay in this map so the admin
// Edition Lock Manager can still label any historical year docs. The
// Full Baseline Diagnostic (`ffp`, formerly Field Forward Plan) sits first in
// Comprehensive Reports, ahead of Current-Year Outlook (`dyo`) and the Land
// Health Reports.
//
// 2026-10-16 (handoffs 56 and 57): the Action Summary ships as its own
// package `asum` (HTML only, kind `companion_html`). Until a property has
// an `asum` edition, the page still opens the older FFP companion file
// through the display-only `ACTIONS_SUMMARY_ID` card. `dyo` is the
// Current-Year Outlook (predicted, provisional), in Comprehensive Reports.
//
// What `PACKAGE_DISPLAY` defines per code
// ---------------------------------------
//   - `label`             — human-readable name shown to users + admins.
//   - `short_description` — one-line tagline for marketing-style cards.
//   - `order`             — relative display order (lower = higher up).
//                           7-code suite gets 1-6, Stripe SKU containers
//                           7-8, legacy archived 90-93. Codes encountered
//                           on a property but missing from this map fall
//                           back to `order: 99` and the raw shortcode as
//                           label (see `packageDisplayFor`).
//
// What this file does NOT define
// ------------------------------
//   - Tier inclusion (which codes belong to STD vs ESR sites). That's
//     in `frontend/src/pages/FarmReportsPage.js` because it's coupled
//     to the per-property entitlement check that depends on user data.
//   - Stripe price ids — those live on `reportPackages/{id}.stripe_*`
//     in Firestore.
//   - Archived flag — comes from `reportPackages/{id}.archived` in
//     Firestore (the catalogue doc takes precedence over this static
//     map for archive-state filtering).
export const PACKAGE_DISPLAY = {
  // ---- 7-code suite (canonical per REPORT_SUITE_HANDOFF §2, 2026-05-19) ----
  lhr:   { label: 'Land Health Report (Standard)', short_description: 'Your full land health diagnostic with ecosystem scores, field breakdown, and risks and opportunities across about twelve sections.', order: 3 },
  'lhr+': { label: 'Land Health Report (ESR)',     short_description: 'The full land health diagnostic plus a sustainability and disclosure section for CSRD, SBTN, and TNFD reporting.', order: 3 },
  esr:   { label: 'ESG Sustainability Edition',    short_description: 'A corporate sustainability report covering ecosystem condition, carbon stocks, and alignment with major ESG disclosure frameworks.', order: 2 },
  lhb:   { label: 'Land Health Brief',             short_description: 'A short summary of your land health verdict, scores, and field diagnosis in about ten pages.', order: 3 },
  sab:   { label: 'Strategic Action Brief',        short_description: 'A decision pack that combines strategic brief, risks, opportunities, and trajectory into one document.', order: 4 },
  fop:   { label: 'Field Operations Profile',      short_description: 'A per-field operational guide for land managers with narrative and seasonal charts.', order: 5 },
  ffp:   { label: 'Full Baseline Diagnostic',       short_description: 'This is the full diagnostic baseline to the end of the last full year including risk and opportunity assessments and per-field action plan: what to do, why here, what to check first, and what each move is expected to change.', order: 1 },
  dyo:   { label: 'Current-Year Outlook',          short_description: 'A predicted, provisional reading of this year\'s land health, as a 1 to 9 level and band, from the data in so far, shown beside the direct reading. Not the audited figure; your assessed year stays the last complete year.', order: 2 },
  vpa:   { label: 'Visual Property Atlas',         short_description: 'A focus on satellite-, topographic and assessment maps with commentary.', order: 7 },
  // Handoff 56: the Action Summary is its own package. One companion_html
  // file, no PDF. The Reports page opens it with ?kind=companion_html.
  asum:  { label: 'Actions Summary',               short_description: 'A short, grouped summary of the actions from your Full Baseline Diagnostic, by the action each field leads with.', order: 4 },
  // ---- Phase 1 Foundation disclosure deliverables ----
  // (AppDevPromptsMngmnt/handoffs/00-foundation.md §C, 2026-06-22). Two new
  // farm-facing reports produced + pushed by ReportBuilder through the SAME
  // publish pipeline as the 7-code suite, so each surfaces as a card here the
  // moment editions land at `farms/{id}/reports/{code}/years/...` — no extra
  // app wiring. `esgr` is DISTINCT from `esr` (ESG Sustainability Edition):
  // `esgr` is the disclosure-READINESS report; `cred` is the shareable
  // supplier credential. Codes use the established lowercase package_id
  // convention; if RB publishes them under a different casing, the editions
  // still render (via the PACKAGE_DISPLAY fallback) — confirm the exact
  // package_id with EcoIntel and align this map if it differs.
  esgr:  { label: 'ESG Readiness Report',          short_description: 'A farm-facing disclosure-readiness report showing where your evidence maps to the frameworks, what is in place, what is not yet, and the next steps to advance it.', order: 7 },
  cred:  { label: 'Supplier Credential',           short_description: 'A shareable credential for supply-chain buyers: what you can say, backed by satellite evidence, with an honest done / still-to-do status page.', order: 8 },
  // ---- v66.2 catalogue Stripe SKUs (Subscription containers) ----
  standard:      { label: 'Standard',  short_description: 'Your annual land health diagnostic suite for farmers and land managers.', order: 7 },
  standard_plus: { label: 'Standard+', short_description: 'The standard suite plus a corporate ESG readiness edition for supply chain reporting.', order: 8 },
  // ---- Legacy four (archived but still rendered if year docs exist) ----
  land_health:        { label: 'Land Health Report (legacy)',           short_description: 'Legacy fixed package, superseded by the 7-code suite.', order: 90 },
  carbon:             { label: 'Carbon Edition (legacy)',               short_description: 'Legacy fixed package; carbon content is now in the Land Health Report.', order: 91 },
  regen_practice:     { label: 'Regenerative Practice Edition (legacy)', short_description: 'Legacy fixed package; practice content is now in the Land Health Report and Strategic Action Brief.', order: 92 },
  sustainability_esg: { label: 'Sustainability / ESG (legacy)',         short_description: 'Legacy fixed package, replaced by the ESG Sustainability Edition (esr).', order: 93 },
};

// Every package code the FE will probe `farms/{id}/reports/{code}/years/`
// for. The customer Reports page uses this map's label and order for known
// codes, so a stale catalogue name or rank cannot put the Land Health Report
// ahead of Full Baseline Diagnostic. We ALWAYS try to read each code's
// `years/` subcollection so a Marcus push under a brand-new shortcode
// renders without requiring an app-side seed update first. Order matches
// PACKAGE_DISPLAY insertion order (the 7-code suite first), which is the
// natural order admins want to see in the lock manager too.
export const KNOWN_PACKAGE_CODES = Object.keys(PACKAGE_DISPLAY);

// Package ids that were retired under the v66.2 doctrine + 2026-05-19
// 7-code-suite rename. They remain in `KNOWN_PACKAGE_CODES` so the
// admin Edition Lock Manager can still surface any historical year
// docs that survived cleanup, BUT the customer-facing Reports page
// must NOT render an empty placeholder card for any of them when no
// editions exist for the property — Marcus's pipeline writes new
// pushes under the 7-code suite, so a legacy code with zero editions
// will *never* receive new content. Empty cards just confuse users
// and contradict v66.2's "only RB-published reports render to
// customers" rule.
//
// `seed-report-packages-v66-2.mjs` ALSO marks each of these as
// `archived: true` in the `reportPackages/` catalogue, but we duplicate
// the list here as a code-level guard so this can never silently
// regress just because the seed didn't run on a fresh deploy.
export const RETIRED_LEGACY_PACKAGE_IDS = new Set([
  'land_health',
  'carbon',
  'regen_practice',
  'sustainability_esg',
]);

// Customer Reports page retirement (2026-10-15). These codes stay in
// `KNOWN_PACKAGE_CODES` so the admin Edition Lock Manager can still open
// historical year docs, but the customer page hides the card even when
// editions exist. Unlike `RETIRED_LEGACY_PACKAGE_IDS`, an existing edition
// does not bring the card back.
export const CUSTOMER_RETIRED_PACKAGE_IDS = new Set([
  'sab', // Strategic Action Brief
  'fop', // Field Operations Profile
]);

// Fallback card for properties whose last publish still shipped the Action
// Summary inside the FFP package (kind `companion_html`). Display-only: there
// is no `farms/{id}/reports/actions_summary/` collection. Once an `asum`
// edition exists, the Reports page uses that package and skips this card.
export const ACTIONS_SUMMARY_ID = 'actions_summary';
export const ACTIONS_SUMMARY_SOURCE_PACKAGE = 'ffp';
export const ACTIONS_SUMMARY_OPEN_KIND = 'companion_html';
export const ASUM_PACKAGE_ID = 'asum';
const ACTIONS_SUMMARY_DISPLAY = {
  label: 'Actions Summary',
  short_description: 'A short, grouped summary of the actions from your Full Baseline Diagnostic, by the action each field leads with.',
  order: 4,
};

// Lookup with fallback. Codes encountered on a property but missing
// from PACKAGE_DISPLAY (e.g. a brand-new shortcode Marcus rolls out
// before this map is updated) render with the raw shortcode as label
// and the lowest possible display priority — so we never silently drop
// a real report.
export function packageDisplayFor(id) {
  if (id === ACTIONS_SUMMARY_ID) return { ...ACTIONS_SUMMARY_DISPLAY, group: 'focused' };
  return PACKAGE_DISPLAY[id] || { label: id, short_description: '', order: 99, group: 'other' };
}

// ---------------------------------------------------------------------------
// Reports page grouping + tile accents (handoffs/02-reports-page.md §A/§C).
//
// COLOURS: every accent below is a `var(--token)` reference into
// src/styles/tokens.css — never a raw hex (core-principles.mdc + the handoff
// rule "map the reference's raw hexes onto the tokens"). Pairs that map to an
// existing brand/disclosure/edx/layer token reuse it directly; the few
// genuinely-new tile tints (Focused teal family + esgr blue) are defined once
// as `--rep-*` tokens in tokens.css and referenced here.
// ---------------------------------------------------------------------------

// Which group each package belongs to. Packages with no entry fall into the
// final "other" group so a code we don't recognise never disappears.
export const PACKAGE_GROUP = {
  lhr: 'comprehensive', 'lhr+': 'comprehensive', ffp: 'comprehensive', dyo: 'comprehensive',
  lhb: 'focused', asum: 'focused', [ACTIONS_SUMMARY_ID]: 'focused',
  esr: 'esg', esgr: 'esg', cred: 'esg',
  vpa: 'supporting',
};

// Per-package tile accent: { iconKey, bg, fg } — bg/fg are token references.
// iconKey selects the SVG in components/ReportPackageIcons.js.
export const PACKAGE_TILE = {
  lhr:   { iconKey: 'lhr',   bg: 'var(--ev-bg)',        fg: 'var(--edx7)' },
  'lhr+':{ iconKey: 'lhr+',  bg: 'var(--rep-focused-bg)', fg: 'var(--edx8)' },
  lhb:   { iconKey: 'lhb',   bg: 'var(--rep-focused-bg)', fg: 'var(--rep-focused-fg)' },
  sab:   { iconKey: 'sab',   bg: 'var(--rep-sab-bg)',   fg: 'var(--teal)' },
  fop:   { iconKey: 'fop',   bg: 'var(--rep-fop-bg)',   fg: 'var(--rep-fop-fg)' },
  ffp:   { iconKey: 'ffp',   bg: 'var(--rep-fop-bg)',   fg: 'var(--rep-fop-fg)' },
  dyo:   { iconKey: 'dyo',   bg: 'var(--pos-bg)',   fg: 'var(--pos)' },
  asum:  { iconKey: 'actions', bg: 'var(--rep-focused-bg)', fg: 'var(--rep-focused-fg)' },
  [ACTIONS_SUMMARY_ID]: { iconKey: 'actions', bg: 'var(--rep-focused-bg)', fg: 'var(--rep-focused-fg)' },
  esr:   { iconKey: 'esr',   bg: 'var(--doc-bg)',       fg: 'var(--doc)' },
  esgr:  { iconKey: 'esgr',  bg: 'var(--rep-esgr-bg)',  fg: 'var(--rep-esgr-fg)' },
  cred:  { iconKey: 'cred',  bg: 'var(--rep-esgr-bg)',  fg: 'var(--layer-blue)' },
  vpa:   { iconKey: 'vpa',   bg: 'var(--nature-bg)',    fg: 'var(--layer-nature)' },
};

export function packageTileFor(id) {
  return PACKAGE_TILE[id] || { iconKey: 'file', bg: 'var(--cream2)', fg: 'var(--fg-muted)' };
}

// The four groups in display order + a trailing "other" bucket. Each group's
// icon bg/fg are token references (see colour note above). `title`/`subtitle`
// copy mirrors the design reference headers.
export const REPORT_GROUPS = [
  {
    id: 'comprehensive',
    title: 'Comprehensive Reports',
    subtitle: 'Full land-health diagnostics, front to back',
    bg: 'var(--ev-bg)', fg: 'var(--edx7)',
  },
  {
    id: 'focused',
    title: 'Focused Short-Reports',
    subtitle: 'Quick reads for fast decisions',
    bg: 'var(--rep-focused-bg)', fg: 'var(--rep-focused-fg)',
  },
  {
    id: 'esg',
    title: 'ESG-related & Disclosure',
    subtitle: 'Corporate sustainability & framework alignment',
    bg: 'var(--doc-bg)', fg: 'var(--doc)',
  },
  {
    id: 'supporting',
    title: 'Supporting Reports',
    subtitle: 'Visual reference material',
    bg: 'var(--nature-bg)', fg: 'var(--layer-nature)',
  },
  {
    id: 'other',
    title: 'Other reports',
    subtitle: 'Additional published editions',
    bg: 'var(--cream2)', fg: 'var(--fg-muted)',
  },
];

export function packageGroupFor(id) {
  return PACKAGE_GROUP[id] || 'other';
}

// The shareable "Share your status" badge (the 1200x630 social card) is
// produced + pushed by ReportBuilder through the SAME publish pipeline as
// reports (AppDevPromptsMngmnt/handoffs/00-foundation.md §D, 2026-06-22), so
// it lives at `farms/{id}/reports/{STATUS_BADGE_PACKAGE_CODE}/years/{year}/...`
// as an image file. It is DELIBERATELY NOT in PACKAGE_DISPLAY /
// KNOWN_PACKAGE_CODES: it must never render as a report card on the Reports
// page — it is surfaced only as the Dashboard "Share your status" affordance,
// resolved on demand via the report-download signed-URL endpoint
// (see api.js → getStatusBadgeUrl). OPEN (decision-needed): confirm the exact
// package_id / file kind / year RB publishes the badge under; this default
// follows the documented filename convention.
export const STATUS_BADGE_PACKAGE_CODE = 'badge';

// Per-property report page pushed by ReportBuilder (`report_page` on
// POST /reports, stored as `farms/{id}.reportPage`). When this block is
// present the Reports page renders these sections and packages verbatim.
// When it is absent, or not usable, the page keeps REPORT_GROUPS and
// PACKAGE_DISPLAY. Known section ids keep their accent. Any other id is a
// custom section and uses the brand bespoke accent. The share-status badge
// is never a report card.
const SECTION_ACCENT = Object.fromEntries(
  REPORT_GROUPS.map((g) => [g.id, { bg: g.bg, fg: g.fg }]),
);

// Deep brand green. Used for every section id RB invents.
export const BESPOKE_SECTION_ACCENT = {
  bg: 'var(--rep-bespoke-bg)',
  fg: 'var(--rep-bespoke-fg)',
  iconKey: 'bespoke',
};

export function sectionAccentFor(sectionId) {
  const known = SECTION_ACCENT[sectionId];
  if (!known) return { ...BESPOKE_SECTION_ACCENT };
  return { bg: known.bg, fg: known.fg, iconKey: sectionId };
}

// Optional `icon` on a report_page package. Keep this list in step with
// GROUP_ICONS / TILE_ICONS in components/ReportPackageIcons.js and with
// REPORT_PAGE_ICON_KEYS in marcusIngestion.js. An omitted or unknown icon
// uses the section icon.
export const REPORT_ICON_KEYS = [
  'bespoke', 'water', 'leaf', 'pin',
  'comprehensive', 'focused', 'esg', 'supporting', 'other',
  'lhr', 'lhr+', 'lhb', 'sab', 'fop', 'ffp', 'dyo', 'actions',
  'esr', 'esgr', 'cred', 'vpa', 'file',
];

const REPORT_ICON_KEY_SET = new Set(REPORT_ICON_KEYS);

// Tile for one catalogue report. A recognised icon is drawn; its designed
// tile colours are used when that icon is a known package tile, otherwise
// it takes the section colour. No icon means the section icon.
export function catalogueTileFor(sectionId, icon) {
  const section = sectionAccentFor(sectionId);
  const key = typeof icon === 'string' ? icon.trim() : '';
  if (!REPORT_ICON_KEY_SET.has(key)) {
    return { iconKey: section.iconKey, bg: section.bg, fg: section.fg };
  }
  if (PACKAGE_TILE[key]) {
    const tile = PACKAGE_TILE[key];
    return { iconKey: tile.iconKey, bg: tile.bg, fg: tile.fg };
  }
  if (key === 'actions') {
    const tile = PACKAGE_TILE[ACTIONS_SUMMARY_ID];
    return { iconKey: 'actions', bg: tile.bg, fg: tile.fg };
  }
  return { iconKey: key, bg: section.bg, fg: section.fg };
}

function reportPageString(value, fallback) {
  return typeof value === 'string' ? value : fallback;
}

/**
 * Read a stored `reportPage`. Returns null when the block is absent or not
 * usable, so the caller falls back to the hardcoded groups. Does not invent
 * titles or descriptions.
 */
export function parseReportPage(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  if (!Array.isArray(raw.sections) || !Array.isArray(raw.packages)) return null;
  const sections = [];
  const sectionIds = new Set();
  for (const s of raw.sections) {
    if (!s || typeof s !== 'object') return null;
    const id = reportPageString(s.id, '').trim();
    if (!id || sectionIds.has(id) || typeof s.title !== 'string') return null;
    const order = Number(s.order);
    if (!Number.isFinite(order)) return null;
    sectionIds.add(id);
    sections.push({
      id,
      title: s.title,
      subtitle: reportPageString(s.subtitle, ''),
      order,
    });
  }
  const packages = [];
  const packageIds = new Set();
  for (const p of raw.packages) {
    if (!p || typeof p !== 'object') return null;
    const packageId = reportPageString(p.package_id, '').trim();
    const sectionId = reportPageString(p.section_id, '').trim();
    if (!packageId || !sectionId || typeof p.label !== 'string') return null;
    if (packageId === STATUS_BADGE_PACKAGE_CODE) return null;
    if (packageIds.has(packageId) || !sectionIds.has(sectionId)) return null;
    const order = Number(p.order);
    if (!Number.isFinite(order)) return null;
    packageIds.add(packageId);
    const icon = reportPageString(p.icon, '').trim();
    const row = {
      package_id: packageId,
      section_id: sectionId,
      label: p.label,
      short_description: reportPageString(p.short_description, ''),
      order,
    };
    if (REPORT_ICON_KEY_SET.has(icon)) row.icon = icon;
    packages.push(row);
  }
  return { sections, packages };
}

// Package ids whose year docs the Reports page should read when a catalogue
// is present. The synthetic Actions Summary has no collection of its own; if
// it is listed, also read the Full Baseline Diagnostic companion it opens.
export function packageIdsToProbe(reportPage) {
  const ids = [];
  const seen = new Set();
  const add = (id) => {
    if (!id || seen.has(id)) return;
    seen.add(id);
    ids.push(id);
  };
  let needsActionsSource = false;
  for (const pkg of reportPage.packages) {
    if (pkg.package_id === ACTIONS_SUMMARY_ID) {
      needsActionsSource = true;
      continue;
    }
    add(pkg.package_id);
  }
  if (needsActionsSource) add(ACTIONS_SUMMARY_SOURCE_PACKAGE);
  return ids;
}

// Edition lock manager: known codes, plus any catalogue package on this
// property that is not the synthetic Actions Summary or the share badge.
export function editionProbeIdsForFarm(farm) {
  const ids = [...KNOWN_PACKAGE_CODES];
  const seen = new Set(ids);
  const page = parseReportPage(farm && farm.reportPage);
  if (!page) return ids;
  for (const pkg of page.packages) {
    if (pkg.package_id === ACTIONS_SUMMARY_ID || pkg.package_id === STATUS_BADGE_PACKAGE_CODE) continue;
    if (seen.has(pkg.package_id)) continue;
    seen.add(pkg.package_id);
    ids.push(pkg.package_id);
  }
  return ids;
}
